import React, { Component, type ErrorInfo, type ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });

    // Send to Sentry (if configured)
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack } },
    });

    // Log to console for debugging
    console.error("[ErrorBoundary] Crash captured:", {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    });

    // Send to backend telemetry (non-blocking)
    this.reportError(error, errorInfo).catch(() => {});
  }

  private async reportError(error: Error, errorInfo: ErrorInfo) {
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const {
        data: { session },
      } = await supabase.auth.getSession();

      await supabase.from("rpa_audit_log").insert({
        user_id: session?.user?.id || "00000000-0000-0000-0000-000000000000",
        action_type: "FRONTEND_CRASH",
        action_description: `[ErrorBoundary] ${error.message}`,
        entity_type: "frontend",
        executed_by: "ERROR_BOUNDARY",
        success: false,
        new_state: {
          error_name: error.name,
          error_message: error.message,
          component_stack: errorInfo.componentStack?.slice(0, 500),
          url: window.location.href,
          timestamp: new Date().toISOString(),
        },
      });
    } catch {
      // Silent fail — don't crash the error handler
    }
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-md w-full text-center space-y-6">
            <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="h-8 w-8 text-destructive" />
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-bold text-foreground">
                Ops! Algo deu errado
              </h2>
              <p className="text-sm text-muted-foreground">
                Nosso sistema detectou um erro inesperado. A equipe técnica já
                foi notificada automaticamente.
              </p>
            </div>
            {this.state.error && (
              <div className="bg-muted/50 rounded-lg p-3 text-left">
                <p className="text-xs font-mono text-muted-foreground break-all">
                  {this.state.error.message}
                </p>
              </div>
            )}
            <div className="flex gap-3 justify-center">
              <Button onClick={this.handleRetry} variant="default">
                <RefreshCw className="h-4 w-4 mr-2" />
                Tentar novamente
              </Button>
              <Button
                onClick={() => (window.location.href = "/")}
                variant="outline"
              >
                Voltar ao início
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
